import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '../../api/client';
import { Project } from '../../types';
import { ProgressBar } from '../../components/ui/ProgressBar';
import { MachineSelector } from '../../components/ui/MachineSelector';
import {
  Layers, Clock, AlertTriangle, CheckCircle2, Search,
  Loader2, Star, Zap, Trophy, ArrowRight, ChevronDown, ChevronUp,
} from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}

function isOverdue(iso: string) {
  return new Date(iso).getTime() < Date.now();
}

function calcPoints(project: Project): number {
  const base = 100;
  const daysLeft = project.dueDate ? daysUntil(project.dueDate) : 7;
  const urgencyBonus = daysLeft <= 0 ? 80 : daysLeft <= 1 ? 50 : daysLeft <= 3 ? 25 : 0;
  return base + urgencyBonus;
}

function urgencyLevel(project: Project): 'critical' | 'high' | 'normal' {
  if (!project.dueDate) return 'normal';
  const d = daysUntil(project.dueDate);
  if (d <= 0) return 'critical';
  if (d <= 2) return 'high';
  return 'normal';
}

const URGENCY_STYLE = {
  critical: { border: '1px solid rgba(239,68,68,0.4)',  glow: '0 0 16px rgba(239,68,68,0.15)',  badge: '#ef4444', badgeBg: 'rgba(239,68,68,0.12)',  label: 'URGENTE'  },
  high:     { border: '1px solid rgba(249,115,22,0.4)', glow: '0 0 16px rgba(249,115,22,0.12)', badge: '#f97316', badgeBg: 'rgba(249,115,22,0.12)', label: 'PRIORIDAD' },
  normal:   { border: '1px solid #1e1e2e',              glow: 'none',                            badge: '#22c55e', badgeBg: 'rgba(34,197,94,0.10)',  label: 'EN COLA'   },
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div style={{
      background: '#12121a', border: '1px solid #1e1e2e', borderRadius: 16,
      padding: 20, display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      {[80, 55, 40, 30].map((w, i) => (
        <div key={i} style={{
          height: i === 0 ? 16 : 12, width: `${w}%`,
          background: '#1c1b28', borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite',
        }} />
      ))}
    </div>
  );
}

// ─── Project Card ─────────────────────────────────────────────────────────────

interface ProjectCardProps {
  project: Project;
  onMarkDone: (id: number) => void;
  isMarkingDone: boolean;
  isMobile: boolean;
}

function ProjectCard({ project, onMarkDone, isMarkingDone, isMobile }: ProjectCardProps) {
  const [hovered, setHovered] = useState(false);
  const [showMachines, setShowMachines] = useState(false);
  const urgency = urgencyLevel(project);
  const style = URGENCY_STYLE[urgency];
  const points = calcPoints(project);
  const days = project.dueDate ? daysUntil(project.dueDate) : null;
  const overdue = project.dueDate && isOverdue(project.dueDate);

  const dtfStep = project.steps.find(s => s.department === 'dtf');
  const assignedMachineId = (dtfStep?.data as any)?.machineId as number | undefined;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#12121a',
        border: hovered ? '1px solid #2e2e42' : style.border,
        borderRadius: 16,
        padding: isMobile ? '16px' : '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        transition: 'all 0.18s cubic-bezier(0.16,1,0.3,1)',
        transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
        boxShadow: hovered ? `0 12px 32px rgba(0,0,0,0.35), ${style.glow}` : style.glow,
        cursor: 'default',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Urgency glow bar at top */}
      {urgency !== 'normal' && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 3,
          background: urgency === 'critical'
            ? 'linear-gradient(90deg, #ef4444, #dc2626)'
            : 'linear-gradient(90deg, #f97316, #ea580c)',
          borderRadius: '16px 16px 0 0',
        }} />
      )}

      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginTop: urgency !== 'normal' ? 4 : 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <span style={{ fontSize: 10, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace' }}>
              {project.reference}
            </span>
            <span style={{
              fontSize: 9, fontFamily: 'JetBrains Mono, monospace', fontWeight: 700,
              padding: '2px 6px', borderRadius: 4, letterSpacing: '0.06em',
              color: style.badge, background: style.badgeBg,
            }}>
              {style.label}
            </span>
          </div>
          <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color: '#e8e6f4', lineHeight: 1.3, marginBottom: 2 }}>
            {project.title}
          </div>
          {project.client && (
            <div style={{ fontSize: 11, color: '#6b6880' }}>
              {project.client.company || project.client.name}
            </div>
          )}
        </div>

        {/* Points badge */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)',
          borderRadius: 10, padding: '6px 10px', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Star size={9} color="#f97316" fill="#f97316" />
            <span style={{ fontSize: 16, fontWeight: 800, color: '#f97316', fontFamily: 'Syne, sans-serif', lineHeight: 1 }}>
              {points}
            </span>
          </div>
          <span style={{ fontSize: 8, color: '#6b6880', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em' }}>
            PTS
          </span>
        </div>
      </div>

      {/* Description */}
      {project.description && (
        <p style={{ fontSize: 12, color: '#6b6880', margin: 0, lineHeight: 1.5, borderLeft: '2px solid #27262f', paddingLeft: 10 }}>
          {project.description}
        </p>
      )}

      {/* Progress */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: '#6b6880', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.04em' }}>
            PROGRESO GLOBAL
          </span>
          <span style={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace', color: '#f97316', fontWeight: 700 }}>
            {project.progress}%
          </span>
        </div>
        <ProgressBar value={project.progress} color="#f97316" height={5} />
      </div>

      {/* Steps pipeline */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        {project.steps.map((step, idx) => {
          const isDtf = step.department === 'dtf';
          const color = step.status === 'done' ? '#22c55e'
            : step.status === 'in_progress' ? '#f97316'
            : '#2e2e42';
          return (
            <React.Fragment key={step.id}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 3,
                background: isDtf ? 'rgba(249,115,22,0.12)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${isDtf ? 'rgba(249,115,22,0.3)' : color + '44'}`,
                borderRadius: 5, padding: '2px 6px',
              }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: color, boxShadow: step.status === 'in_progress' ? `0 0 5px ${color}` : 'none' }} />
                <span style={{ fontSize: 9, fontFamily: 'JetBrains Mono, monospace', color: isDtf ? '#f97316' : color, fontWeight: isDtf ? 700 : 500 }}>
                  {step.department.toUpperCase()}
                </span>
              </div>
              {idx < project.steps.length - 1 && (
                <ArrowRight size={9} color="#2e2e42" style={{ flexShrink: 0 }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Machine selector toggle */}
      {dtfStep && (
        <div>
          <button
            onClick={() => setShowMachines(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, width: '100%',
              background: assignedMachineId
                ? 'rgba(249,115,22,0.06)'
                : 'rgba(249,115,22,0.04)',
              border: assignedMachineId
                ? '1px solid rgba(249,115,22,0.25)'
                : '1px solid #1e1e2e',
              borderRadius: 10, padding: '10px 14px',
              cursor: 'pointer', transition: 'all 0.15s',
              justifyContent: 'space-between',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(249,115,22,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = assignedMachineId ? 'rgba(249,115,22,0.25)' : '#1e1e2e'; }}
            aria-expanded={showMachines}
            aria-label="Mostrar selector de máquina"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <Layers size={13} color={assignedMachineId ? '#f97316' : '#6b6880'} />
              <span style={{
                fontSize: 11, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600,
                color: assignedMachineId ? '#f97316' : '#6b6880',
                letterSpacing: '0.04em',
              }}>
                {assignedMachineId ? 'MÁQUINA ASIGNADA' : 'ASIGNAR MÁQUINA'}
              </span>
            </div>
            {showMachines
              ? <ChevronUp size={13} color="#6b6880" />
              : <ChevronDown size={13} color="#6b6880" />
            }
          </button>

          {showMachines && (
            <div style={{ marginTop: 10 }}>
              <MachineSelector
                stepId={dtfStep.id}
                currentMachineId={assignedMachineId}
                department="DTF"
              />
            </div>
          )}
        </div>
      )}

      {/* Footer: date + action */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, paddingTop: 6, borderTop: '1px solid #1a1a28' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {project.dueDate ? (
            <>
              {overdue
                ? <AlertTriangle size={11} color="#ef4444" />
                : days !== null && days <= 2
                ? <Zap size={11} color="#f97316" />
                : <Clock size={11} color="#6b6880" />
              }
              <span style={{
                fontSize: 11, fontFamily: 'JetBrains Mono, monospace',
                color: overdue ? '#ef4444' : days !== null && days <= 2 ? '#f97316' : '#6b6880',
                fontWeight: overdue || (days !== null && days <= 2) ? 600 : 400,
              }}>
                {overdue
                  ? `VENCIDO hace ${Math.abs(days ?? 0)} día${Math.abs(days ?? 0) !== 1 ? 's' : ''}`
                  : days === 0
                  ? 'VENCE HOY'
                  : `${formatDate(project.dueDate)}`
                }
              </span>
            </>
          ) : (
            <span style={{ fontSize: 11, color: '#4a4858' }}>Sin fecha límite</span>
          )}
        </div>

        <button
          onClick={() => onMarkDone(project.id)}
          disabled={isMarkingDone}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: isMarkingDone ? '#1c1b28' : 'linear-gradient(135deg, #22c55e, #16a34a)',
            border: 'none', borderRadius: 8,
            padding: isMobile ? '7px 12px' : '8px 14px',
            color: isMarkingDone ? '#6b6880' : '#fff',
            fontSize: 11, fontWeight: 700, cursor: isMarkingDone ? 'not-allowed' : 'pointer',
            fontFamily: 'DM Sans, sans-serif', letterSpacing: '0.02em',
            boxShadow: isMarkingDone ? 'none' : '0 3px 10px rgba(34,197,94,0.3)',
            transition: 'all 0.15s',
            flexShrink: 0,
          }}
          aria-label={`Marcar ${project.title} como completado en DTF`}
        >
          {isMarkingDone ? (
            <Loader2 size={12} style={{ animation: 'spin 0.8s linear infinite' }} />
          ) : (
            <CheckCircle2 size={12} />
          )}
          {isMarkingDone ? 'Guardando...' : 'Hecho'}
        </button>
      </div>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '64px 24px', textAlign: 'center' }}>
      <div style={{
        width: 72, height: 72, borderRadius: 20,
        background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.15)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Layers size={30} color="#f97316" style={{ opacity: 0.5 }} />
      </div>
      <div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#c4c2d4', marginBottom: 6, fontFamily: 'Syne, sans-serif' }}>
          Cola vacía
        </div>
        <div style={{ fontSize: 13, color: '#4a4858', maxWidth: 280 }}>
          No hay trabajos DTF pendientes en este momento.
        </div>
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)',
        borderRadius: 8, padding: '8px 14px',
      }}>
        <Trophy size={13} color="#22c55e" />
        <span style={{ fontSize: 12, color: '#22c55e', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
          TODOS LOS TRABAJOS COMPLETADOS
        </span>
      </div>
    </div>
  );
}

// ─── Queue Stats Bar ──────────────────────────────────────────────────────────

function QueueStatsBar({ projects }: { projects: Project[] }) {
  const total    = projects.length;
  const critical = projects.filter(p => urgencyLevel(p) === 'critical').length;
  const high     = projects.filter(p => urgencyLevel(p) === 'high').length;
  const normal   = projects.filter(p => urgencyLevel(p) === 'normal').length;
  const totalPts = projects.reduce((sum, p) => sum + calcPoints(p), 0);

  const statItems = [
    { label: 'EN COLA',   value: total,    color: '#e8e6f4', icon: <Layers size={13} color="#f97316" /> },
    { label: 'URGENTES',  value: critical, color: '#ef4444', icon: <AlertTriangle size={13} color="#ef4444" /> },
    { label: 'PRIORIDAD', value: high,     color: '#f97316', icon: <Zap size={13} color="#f97316" /> },
    { label: 'NORMALES',  value: normal,   color: '#22c55e', icon: <CheckCircle2 size={13} color="#22c55e" /> },
    { label: 'PTS TOTALES', value: totalPts, color: '#f97316', icon: <Star size={13} color="#f97316" fill="#f97316" /> },
  ];

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
      gap: 10, marginBottom: 24,
    }}>
      {statItems.map(({ label, value, color, icon }) => (
        <div key={label} style={{
          background: '#12121a', border: '1px solid #1e1e2e', borderRadius: 12,
          padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {icon}
            <span style={{ fontSize: 9, fontFamily: 'JetBrains Mono, monospace', color: '#4a4858', letterSpacing: '0.06em' }}>
              {label}
            </span>
          </div>
          <span style={{ fontSize: 22, fontWeight: 900, color, fontFamily: 'Syne, sans-serif', lineHeight: 1 }}>
            {value}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Main DTFPage ─────────────────────────────────────────────────────────────

export default function DTFPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [markingId, setMarkingId] = useState<number | null>(null);
  const [completedIds, setCompletedIds] = useState<Set<number>>(new Set());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const { data: apiProjects, isLoading } = useQuery({
    queryKey: ['projects', 'dtf'],
    queryFn: () => projectsApi.list('dtf'),
    retry: false,
    staleTime: 30_000,
  });

  const allProjects: Project[] = useMemo(() => {
    const raw = (apiProjects as { data: Project[]; total: number } | undefined)?.data ?? [];
    if (raw.length > 0) {
      return raw.filter(p => p.currentDepartment === 'dtf' && p.status !== 'done' && p.status !== 'cancelled');
    }
    return [];
  }, [apiProjects]);

  const projects = useMemo(
    () => allProjects.filter(p => !completedIds.has(p.id)),
    [allProjects, completedIds]
  );

  const markDoneMutation = useMutation({
    mutationFn: async (projectId: number) => {
      const project = allProjects.find(p => p.id === projectId);
      if (!project) throw new Error('Proyecto no encontrado');
      const dtfStep = project.steps.find(s => s.department === 'dtf');
      if (!dtfStep) throw new Error('No hay pasos DTF en este proyecto');
      return projectsApi.updateStep(projectId, dtfStep.id, { status: 'done' });
    },
    onSuccess: (_data, projectId) => {
      setCompletedIds(prev => new Set([...prev, projectId]));
      setMarkingId(null);
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: () => {
      setMarkingId(null);
    },
  });

  const handleMarkDone = useCallback((id: number) => {
    setMarkingId(id);
    markDoneMutation.mutate(id);
  }, [markDoneMutation]);

  const filtered = useMemo(() => {
    if (!search.trim()) return projects;
    const q = search.toLowerCase();
    return projects.filter(p =>
      p.title.toLowerCase().includes(q) ||
      p.reference.toLowerCase().includes(q) ||
      p.client?.name.toLowerCase().includes(q) ||
      p.client?.company?.toLowerCase().includes(q)
    );
  }, [projects, search]);

  const sorted = useMemo(() => {
    const order = { critical: 0, high: 1, normal: 2 };
    return [...filtered].sort((a, b) => {
      const uDiff = order[urgencyLevel(a)] - order[urgencyLevel(b)];
      if (uDiff !== 0) return uDiff;
      if (a.dueDate && b.dueDate) return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      return 0;
    });
  }, [filtered]);

  const gridCols = isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))';

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '32px', minHeight: '100vh', background: '#0a0a0f', fontFamily: 'DM Sans, sans-serif' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Layers size={18} color="#f97316" />
          </div>
          <div>
            <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 900, fontSize: isMobile ? 22 : 26, color: '#fff', margin: 0, letterSpacing: '-0.03em' }}>
              Panel DTF
            </h1>
            <p style={{ fontSize: 12, color: '#6b6880', margin: 0, fontFamily: 'JetBrains Mono, monospace' }}>
              TRABAJOS ASIGNADOS A DTF
            </p>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      {!isLoading && <QueueStatsBar projects={projects} />}

      {/* Search */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: '#12121a', border: '1px solid #1e1e2e', borderRadius: 10,
        padding: '10px 14px', marginBottom: 20,
      }}>
        <Search size={14} color="#6b6880" />
        <input
          type="text"
          placeholder="Buscar por nombre, referencia o cliente..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            background: 'transparent', border: 'none', outline: 'none',
            color: '#e2e0f0', fontSize: 13, width: '100%',
            fontFamily: 'DM Sans, sans-serif',
          }}
        />
        {search && (
          <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', color: '#4a4858', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: 14 }}>
            ×
          </button>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 16 }}>
          {[1, 2, 3].map(i => <SkeletonCard key={i} />)}
        </div>
      ) : sorted.length === 0 ? (
        search ? (
          <div style={{ textAlign: 'center', padding: '48px 16px', color: '#4a4858', fontSize: 14 }}>
            No se encontraron trabajos con "{search}"
          </div>
        ) : (
          <EmptyState />
        )
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 16 }}>
          {sorted.map(project => (
            <ProjectCard
              key={project.id}
              project={project}
              onMarkDone={handleMarkDone}
              isMarkingDone={markingId === project.id}
              isMobile={isMobile}
            />
          ))}
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
      `}</style>
    </div>
  );
}
